import { XMLGroup as IXMLGroup } from '@/types';

/**
 * XMLGroup model representing a group in the XML structure
 */
export class XMLGroupModel implements IXMLGroup {
  element: Element;
  type: 'pedGroups' | 'vehGroups';
  name: string;
  models: string[];

  constructor(element: Element, type: 'pedGroups' | 'vehGroups') {
    this.element = element;
    this.type = type;
    this.name = this.getName();
    this.models = this.getModels();
  }

  /**
   * Get the name of the group
   */
  getName(): string {
    const nameElement = this.element.querySelector('Name') || this.element.querySelector('n');
    return nameElement ? nameElement.textContent || '' : '';
  }

  /**
   * Get all models in the group
   */
  getModels(): string[] {
    const modelElements = this.element.querySelectorAll('models > Item > Name, models > Item > n');
    return Array.from(modelElements).map(el => el.textContent || '').filter(Boolean);
  }

  /**
   * Add a model to the group
   */
  addModel(modelName: string, xmlDoc: Document): boolean {
    const modelsContainer = this.element.querySelector('models');
    if (modelsContainer) {
      const newModel = xmlDoc.createElement('Item');
      
      // Use 'Name' element to match XML format
      const newModelName = xmlDoc.createElement('Name');
      newModelName.textContent = modelName;
      newModel.appendChild(newModelName);
      
      // Add Variations element (required in XML structure)
      const variations = xmlDoc.createElement('Variations');
      newModel.appendChild(variations);
      
      modelsContainer.appendChild(newModel);
      this.models.push(modelName);
      return true;
    }
    return false;
  }

  /**
   * Remove a model from the group
   */
  removeModel(modelName: string): boolean {
    const modelElements = this.element.querySelectorAll('models > Item > Name, models > Item > n');
    for (const element of modelElements) {
      if (element.textContent === modelName) {
        const parentElement = element.parentNode;
        if (parentElement && parentElement.parentNode) {
          parentElement.parentNode.removeChild(parentElement);
        }
        this.models = this.models.filter(m => m !== modelName);
        return true;
      }
    }
    return false;
  }

  /**
   * Check for duplicate models in the group
   */
  checkForDuplicates(): string[] {
    const modelCounts: { [key: string]: number } = {};
    
    this.models.forEach(model => {
      modelCounts[model] = (modelCounts[model] || 0) + 1;
    });

    return Object.keys(modelCounts).filter(model => modelCounts[model] > 1);
  }

  /**
   * Get model count
   */
  getModelCount(): number {
    return this.models.length;
  }

  /**
   * Check if model exists in group
   */
  hasModel(modelName: string): boolean {
    return this.models.includes(modelName);
  }

  /**
   * Update internal models array from DOM
   */
  refreshModels(): void {
    this.models = this.getModels();
  }
}